import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { blobToBase64 } from './blobToBase64';
import {
  ALLOWED_EXTENSIONS,
  assertMimeTypeMatchesFilename,
  MimeType,
} from './fileValidation';
import { CapacitorBridge } from '@/services/CapacitorBridge';
import { createTypedBlob } from './triggerBlobDownload';

/**
 * Opens a file blob for the user to view.
 *
 * On **web**, creates a blob URL and opens it in a new browser tab (images render
 * inline; PDFs use the browser PDF viewer). The URL is revoked after a short delay.
 *
 * On **native** (Capacitor), writes the file to device storage and opens it with the
 * system file viewer. Unsupported types on iOS may fall back to the share sheet.
 *
 * @param blob - File content (may be re-wrapped if `blob.type` does not match `mimeType`)
 * @param filename - Used for MIME validation, native file path, and error dialogs
 * @param mimeType - Must match the filename extension; mismatches are logged to console
 */
export async function openFileFromBlob(
  blob: Blob,
  filename: string,
  mimeType: MimeType,
): Promise<void> {
  assertMimeTypeMatchesFilename(filename, mimeType);

  const typedBlob =
    blob.type === mimeType
      ? blob
      : createTypedBlob(await blob.arrayBuffer(), filename, mimeType);

  if (Capacitor.getPlatform() === 'web') {
    const fileURL = URL.createObjectURL(typedBlob);
    window.open(fileURL);
    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    return;
  }

  const base64 = await blobToBase64(typedBlob);

  try {
    await CapacitorBridge.execute('requestPermissions');
  } catch (e) {
    console.warn('Permission request failed or rejected', e);
  }

  const { uri } = await CapacitorBridge.execute('writeFile', {
    path: filename,
    data: base64,
    isBinary: true,
  });

  if (Capacitor.getPlatform() === 'ios') {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext && !ALLOWED_EXTENSIONS.includes(ext)) {
      await Share.share({
        title: filename,
        url: uri,
      });
      return;
    }
  }

  try {
    await CapacitorBridge.execute('openFile', {
      filePath: uri,
    });
  } catch (error: unknown) {
    const err = error as { code?: string };
    if (err.code === 'OS-PLUG-FLVW-0010') {
      const { dispatchFileOpenError } =
        await import('../components/common/FileOpenErrorDialog');
      dispatchFileOpenError(filename, uri);
    } else {
      throw error;
    }
  }
}
